/**
 * Recover from stale HTML / missing webpack chunks after a deploy.
 *
 * Session budget (tab sessionStorage):
 *   1) soft reload
 *   2) hard navigation with cache-bust query (preserve hash)
 *   then show a recoverable error screen (no infinite spinner).
 *
 * First-party <script>/<link> failures are also handled early by
 * public/asset-load-error-handler.v2.js (same STORAGE_KEY / budget). Share
 * window.__adsDeployRecoveryInFlight so both layers do not double-count.
 */

export const DEPLOY_RECOVERY_STORAGE_KEY = "adswebui_deploy_asset_recovery";
/** @deprecated migrated into DEPLOY_RECOVERY_STORAGE_KEY */
export const LEGACY_DEPLOY_RECOVERY_STORAGE_KEY =
  "adswebui_stylesheet_reload";
export const MAX_DEPLOY_RECOVERY_ATTEMPTS = 2;
export const CACHE_BUST_QUERY_PARAM = "_ads_deploy_recover";

/** Suspense fallback: if a lazy tree never resolves, run deploy recovery. */
export const DEPLOY_SUSPENSE_WATCHDOG_MS = 20000;

export const DEPLOY_ASSET_LOAD_ERROR_COPY =
  "This page couldn’t load. Please refresh to get the latest version.";

let recoveryInFlight = false;
let errorScreenShown = false;
let exhaustedReported = false;

export const getDeployRecoveryCount = () => {
  try {
    const current = sessionStorage.getItem(DEPLOY_RECOVERY_STORAGE_KEY);
    if (current != null) {
      return parseInt(current, 10) || 0;
    }
    const legacy = sessionStorage.getItem(LEGACY_DEPLOY_RECOVERY_STORAGE_KEY);
    if (legacy != null) {
      sessionStorage.setItem(DEPLOY_RECOVERY_STORAGE_KEY, legacy);
      sessionStorage.removeItem(LEGACY_DEPLOY_RECOVERY_STORAGE_KEY);
      return parseInt(legacy, 10) || 0;
    }
  } catch (_e) {
    // ignore
  }
  return 0;
};

export const setDeployRecoveryCount = (count) => {
  try {
    sessionStorage.setItem(
      DEPLOY_RECOVERY_STORAGE_KEY,
      String(Math.max(0, count)),
    );
    sessionStorage.removeItem(LEGACY_DEPLOY_RECOVERY_STORAGE_KEY);
  } catch (_e) {
    // ignore
  }
};

export const resetDeployRecoveryBudget = () => {
  setDeployRecoveryCount(0);
  recoveryInFlight = false;
  errorScreenShown = false;
  exhaustedReported = false;
  try {
    window.__adsDeployRecoveryInFlight = false;
  } catch (_e) {
    // ignore
  }
};

export const isFirstPartyScriptChunkUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return false;
    return /\.js$/i.test(parsed.pathname);
  } catch (_e) {
    return false;
  }
};

export const isDeployStaleStylesheetUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return false;
    // MiniCssExtract emits both *.styles.css and *.fonts.css (icon/vendor CSS).
    return /\.(?:styles|fonts)\.css$/i.test(parsed.pathname);
  } catch (_e) {
    return false;
  }
};

export const isDeployStaleAssetUrl = (url, { isScript, isLink } = {}) =>
  Boolean(
    (isLink && isDeployStaleStylesheetUrl(url)) ||
      (isScript && isFirstPartyScriptChunkUrl(url)),
  );

export const isChunkLoadFailureReason = (reason) => {
  if (!reason) return false;
  if (reason.name === "ChunkLoadError") return true;
  const msg = String(reason.message || reason);
  return (
    /Loading chunk [\w-]+ failed/i.test(msg) || /chunk load/i.test(msg)
  );
};

export const buildCacheBustDocumentUrl = (href = window.location.href) => {
  const url = new URL(href);
  url.searchParams.set(CACHE_BUST_QUERY_PARAM, String(Date.now()));
  return url.toString();
};

const RELOAD_FETCH = { cache: "reload", credentials: "same-origin" };

const collectPrimingAssetUrls = (doc, baseUrl) => {
  const urls = [];
  doc.querySelectorAll("script[src]").forEach((el) => {
    const src = el.getAttribute("src");
    if (src) {
      urls.push(new URL(src, baseUrl).toString());
    }
  });
  doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((el) => {
    const href = el.getAttribute("href");
    if (href && isDeployStaleStylesheetUrl(new URL(href, baseUrl).toString())) {
      urls.push(new URL(href, baseUrl).toString());
    }
  });
  return urls;
};

/**
 * Reload-fetch HTML + entry scripts/stylesheets before manual Refresh navigation
 * so browsers drop cached 404s for hashed webpack assets (DE-9590).
 * Keep in sync with public/asset-load-error-handler.v2.js.
 */
export const primeDeployRecoveryAssets = async (
  documentUrl,
  { fetchImpl } = {},
) => {
  const doFetch = fetchImpl ?? globalThis.fetch;
  if (typeof doFetch !== "function") {
    return;
  }
  try {
    const response = await doFetch(documentUrl, RELOAD_FETCH);
    if (!response.ok) {
      return;
    }
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const assetUrls = collectPrimingAssetUrls(doc, documentUrl);
    await Promise.all(
      assetUrls.map((assetUrl) =>
        doFetch(assetUrl, RELOAD_FETCH).catch(() => undefined),
      ),
    );
  } catch (_e) {
    // Navigation still proceeds in the Refresh handler.
  }
};

export const stripCacheBustParamFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CACHE_BUST_QUERY_PARAM)) return;
    url.searchParams.delete(CACHE_BUST_QUERY_PARAM);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, "", next);
  } catch (_e) {
    // ignore
  }
};

const defaultNavigate = ({ mode }) => {
  if (mode === "cacheBust") {
    window.location.replace(buildCacheBustDocumentUrl());
    return;
  }
  window.location.reload();
};

/**
 * @returns {"reloaded" | "exhausted" | "in_flight"}
 */
const isGlobalRecoveryInFlight = () => {
  try {
    return Boolean(window.__adsDeployRecoveryInFlight);
  } catch (_e) {
    return false;
  }
};

const setGlobalRecoveryInFlight = (value) => {
  try {
    window.__adsDeployRecoveryInFlight = Boolean(value);
  } catch (_e) {
    // ignore
  }
};

export const tryDeployStaleAssetRecovery = ({
  navigate = defaultNavigate,
} = {}) => {
  // Shared with public/asset-load-error-handler.v2.js so early + hashed
  // listeners do not burn two budget attempts on one failure.
  if (recoveryInFlight || isGlobalRecoveryInFlight()) {
    return "in_flight";
  }
  const count = getDeployRecoveryCount();
  if (count >= MAX_DEPLOY_RECOVERY_ATTEMPTS) {
    return "exhausted";
  }

  recoveryInFlight = true;
  setGlobalRecoveryInFlight(true);
  const nextCount = count + 1;
  setDeployRecoveryCount(nextCount);

  // 1st attempt: soft reload; 2nd: cache-bust hard navigation
  navigate({ mode: nextCount === 1 ? "soft" : "cacheBust" });
  return "reloaded";
};

export const showDeployAssetLoadErrorScreen = ({
  rootId = "root",
  onRefresh,
  /** Test seam — defaults to window.location.replace */
  locationReplace,
  /** Test seam — defaults to global fetch when available */
  fetchImpl,
} = {}) => {
  if (errorScreenShown) {
    return false;
  }
  errorScreenShown = true;

  const root = document.getElementById(rootId);
  if (!root) {
    return false;
  }

  root.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.setAttribute("data-testid", "deploy-asset-load-error");
  wrap.setAttribute("role", "alert");
  wrap.style.cssText =
    "min-height:100vh;display:flex;flex-direction:column;" +
    "align-items:center;justify-content:center;padding:24px;" +
    "font-family:system-ui,-apple-system,sans-serif;text-align:center;" +
    "box-sizing:border-box;color:var(--gray-900-alt);";

  const message = document.createElement("p");
  message.textContent = DEPLOY_ASSET_LOAD_ERROR_COPY;
  message.style.cssText =
    "max-width:28rem;margin:0 0 1.25rem;font-size:1.05rem;line-height:1.45;";

  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Refresh";
  button.setAttribute("aria-label", "Refresh to load the latest version");
  button.style.cssText =
    "cursor:pointer;padding:0.65rem 1.25rem;font-size:1rem;" +
    "border:1px solid var(--gray-800-alt);border-radius:4px;background:var(--color-white);color:var(--gray-900);";

  button.addEventListener("click", () => {
    if (typeof onRefresh === "function") {
      onRefresh();
      return;
    }
    resetDeployRecoveryBudget();
    const replace =
      typeof locationReplace === "function"
        ? locationReplace
        : (url) => window.location.replace(url);
    const nextUrl = buildCacheBustDocumentUrl();
    void primeDeployRecoveryAssets(nextUrl, {
      fetchImpl: fetchImpl ?? globalThis.fetch,
    }).finally(() => {
      replace(nextUrl);
    });
  });

  wrap.appendChild(message);
  wrap.appendChild(button);
  root.appendChild(wrap);
  return true;
};

/**
 * Run recovery, or show error UI + optional report when budget is exhausted.
 * @returns {"reloaded" | "exhausted" | "in_flight"}
 */
export const handleDeployStaleAssetFailure = ({
  reportExhausted,
  navigate,
} = {}) => {
  const result = tryDeployStaleAssetRecovery({ navigate });
  if (result === "exhausted") {
    showDeployAssetLoadErrorScreen();
    if (typeof reportExhausted === "function" && !exhaustedReported) {
      exhaustedReported = true;
      reportExhausted();
    }
  }
  return result;
};

/** Test-only: reset module locks between specs. */
export const __resetDeployRecoveryGuardsForTests = () => {
  recoveryInFlight = false;
  errorScreenShown = false;
  exhaustedReported = false;
  try {
    window.__adsDeployRecoveryInFlight = false;
  } catch (_e) {
    // ignore
  }
};

/** Cypress AUT hook — only when E2E flag is set (see cypress/support/e2e.js). */
const installCypressHooks = () => {
  try {
    if (typeof window === "undefined") return;
    const isAut =
      window.__ADS_CYPRESS_AUT === true ||
      typeof window.Cypress !== "undefined";
    if (!isAut) return;
    window.__adsDeployStaleAssetRecovery = {
      DEPLOY_RECOVERY_STORAGE_KEY,
      CACHE_BUST_QUERY_PARAM,
      DEPLOY_ASSET_LOAD_ERROR_COPY,
      MAX_DEPLOY_RECOVERY_ATTEMPTS,
      DEPLOY_SUSPENSE_WATCHDOG_MS,
      handleDeployStaleAssetFailure,
      tryDeployStaleAssetRecovery,
      getDeployRecoveryCount,
      setDeployRecoveryCount,
      resetDeployRecoveryBudget,
      showDeployAssetLoadErrorScreen,
      __resetDeployRecoveryGuardsForTests,
    };
  } catch (_e) {
    // ignore
  }
};

installCypressHooks();
